import { useEffect, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuth } from '../hooks/useAuth';
import { TOKEN_KEY, consumePostLoginRedirect } from '../services/authService';

export default function Login() {
  const navigate        = useNavigate();
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const ssoInProgress   = inProgress !== InteractionStatus.None;
  const { loginWithPassword } = useAuth();

  const [authError, setAuthError] = useState('');
  const [btnHover,  setBtnHover]  = useState(false);

  // Username/password login (local, non-SSO accounts)
  const [identifier, setIdentifier] = useState('');
  const [password,   setPassword]   = useState('');
  const [pwLoading,  setPwLoading]  = useState(false);

  // Diagnostic — shows in DevTools console
  const cfg = (instance as any).config;
  console.log('[Login] mounted');
  console.log('[Login] clientId:', cfg?.auth?.clientId || '(empty — Azure env vars missing from build)');
  console.log('[Login] redirectUri:', cfg?.auth?.redirectUri);
  console.log('[Login] isAuthenticated:', isAuthenticated, '| inProgress:', inProgress);

  // Already authenticated → skip straight to dashboard.
  // Guard on an ACTUAL app token, not just MSAL's cached session: if the app
  // session died (401) the api interceptor hard-redirects here after wiping the
  // token; without this guard we'd bounce straight back to /dashboard and loop
  // (the "blinking"). With no app token we stay here so the user can re-sign-in.
  useEffect(() => {
    const hasAppToken = !!(window.__AUTH_TOKEN__ || sessionStorage.getItem(TOKEN_KEY));
    if (isAuthenticated && inProgress === InteractionStatus.None && hasAppToken) {
      const to = consumePostLoginRedirect();
      console.log('[Login] already authenticated — redirecting to', to);
      navigate(to, { replace: true });
    }
  }, [isAuthenticated, inProgress, navigate]);

  const handleSSO = async () => {
    console.log('[Login] Sign-in button clicked | inProgress:', inProgress);
    if (ssoInProgress) {
      console.warn('[Login] Interaction already in progress — ignoring click');
      return;
    }
    setAuthError('');
    try {
      await instance.loginRedirect({
        scopes: ['openid', 'profile', 'email', 'User.Read'],
        // No `prompt` here — let Azure AD silently reuse its own browser SSO
        // session when one exists (same as Outlook/Teams), so signing back in
        // after this app's own idle logout doesn't re-demand credentials/MFA.
        // How long that session lasts before Azure AD re-challenges on its own
        // is the tenant's Conditional Access "sign-in frequency" policy in
        // Entra ID, not something this app controls.
      });
    } catch (err: any) {
      console.error('[Login] loginRedirect threw:', err);
      setAuthError(err?.message ?? String(err));
    }
  };

  const handlePasswordLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (pwLoading) return;
    setAuthError('');
    if (!identifier.trim() || !password) {
      setAuthError('Enter your username/email and password.');
      return;
    }
    setPwLoading(true);
    try {
      await loginWithPassword(identifier.trim(), password);
      navigate(consumePostLoginRedirect(), { replace: true });
    } catch (err: any) {
      setAuthError(err?.response?.data?.message ?? 'Sign in failed. Please try again.');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #010E23 0%, #0A1F45 40%, #26148C 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* Large decorative circle — top right */}
      <div style={{
        position: 'absolute',
        width: 500,
        height: 500,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(91,33,255,0.15) 0%, transparent 70%)',
        top: -200,
        right: -200,
        pointerEvents: 'none',
      }}/>

      {/* Large decorative circle — bottom left */}
      <div style={{
        position: 'absolute',
        width: 400,
        height: 400,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(134,233,255,0.08) 0%, transparent 70%)',
        bottom: -150,
        left: -150,
        pointerEvents: 'none',
      }}/>

      {/* Subtle dot-grid overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
        pointerEvents: 'none',
      }}/>

      {/* ── Login card ─────────────────────────────────────────────────────── */}
      <div style={{
        background: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(20px)',
        borderRadius: 20,
        padding: 'clamp(24px, 5vw, 36px) clamp(20px, 6vw, 40px)',
        width: 'min(380px, calc(100vw - 32px))',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.2)',
        position: 'relative',
        zIndex: 10,
      }}>

        {/* ── Brand section ──────────────────────────────────────────────── */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {/* Permus logo card */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'white',
            borderRadius: 16,
            padding: '12px 28px',
            boxShadow: '0 4px 20px rgba(38,20,140,0.1), 0 1px 4px rgba(0,0,0,0.06)',
            border: '1px solid rgba(38,20,140,0.07)',
            marginBottom: 16,
          }}>
            <img
              src="/permus-logo-navy.png"
              alt="Permus Software House"
              onError={e => {
                const el = e.currentTarget;
                el.style.display = 'none';
                const fb = el.nextElementSibling as HTMLElement | null;
                if (fb) fb.style.display = 'flex';
              }}
              style={{ height: 40, width: 'auto', objectFit: 'contain', display: 'block' }}
            />
            <div style={{ display: 'none', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#010E23', letterSpacing: '-0.02em', lineHeight: 1 }}>PERMUS</span>
              <span style={{ fontSize: 8, fontWeight: 600, color: '#26148C', letterSpacing: '0.18em', textTransform: 'uppercase' as const }}>SOFTWARE HOUSE</span>
            </div>
          </div>

          {/* Service•Desk wordmark */}
          <div style={{ textAlign: 'center', margin: '8px 0 4px' }}>
            <span style={{
              fontFamily: "'Plus Jakarta Sans', 'Inter', 'Segoe UI', sans-serif",
              fontWeight: 800,
              fontSize: '22px',
              letterSpacing: '-0.5px',
              color: '#010E23',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0px',
            }}>
              <span style={{ color: '#010E23' }}>Service</span>
              <span style={{
                display: 'inline-block',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#26148C',
                margin: '0 1px 6px 1px',
                flexShrink: 0,
              }} />
              <span style={{ color: '#26148C' }}>Desk</span>
            </span>
          </div>
        </div>

        {/* Sign-in prompt */}
        <p style={{
          textAlign: 'center',
          fontSize: 14,
          color: '#6b7280',
          lineHeight: 1.6,
          marginBottom: 20,
        }}>
          Sign in with your Microsoft 365 account, or your portal username and password.
        </p>

        {/* Error message */}
        {authError && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 16,
          }}>
            <p style={{ color: '#b91c1c', fontSize: 12, lineHeight: 1.5, textAlign: 'center' }}>
              {authError}
            </p>
          </div>
        )}

        {/* Sign in with Microsoft button */}
        <button
          onClick={handleSSO}
          disabled={ssoInProgress}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          style={{
            width: '100%',
            padding: '12px 24px',
            background: ssoInProgress ? '#4a3a9e' : btnHover ? '#1a0f6e' : '#26148C',
            color: 'white',
            border: 'none',
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 600,
            cursor: ssoInProgress ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 15px rgba(38,20,140,0.4)',
            opacity: ssoInProgress ? 0.8 : 1,
          }}
        >
          {ssoInProgress ? (
            <div
              className="animate-spin"
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: '2.5px solid rgba(255,255,255,0.3)',
                borderTopColor: 'white',
              }}
            />
          ) : (
            <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden>
              <rect x="1"  y="1"  width="9" height="9" fill="#F25022"/>
              <rect x="11" y="1"  width="9" height="9" fill="#7FBA00"/>
              <rect x="1"  y="11" width="9" height="9" fill="#00A4EF"/>
              <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
            </svg>
          )}
          {ssoInProgress ? 'Redirecting…' : 'Sign in with Microsoft'}
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          <span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>or</span>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
        </div>

        {/* Username / password login (local accounts) */}
        <form onSubmit={handlePasswordLogin}>
          <input
            type="text"
            value={identifier}
            onChange={e => setIdentifier(e.target.value)}
            placeholder="Username or email"
            autoComplete="off"
            style={{
              width: '100%', padding: '11px 14px', marginBottom: 10, fontSize: 14,
              border: '1px solid #d1d5db', borderRadius: 10, outline: 'none', boxSizing: 'border-box',
            }}
          />
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            autoComplete="new-password"
            style={{
              width: '100%', padding: '11px 14px', marginBottom: 14, fontSize: 14,
              border: '1px solid #d1d5db', borderRadius: 10, outline: 'none', boxSizing: 'border-box',
            }}
          />
          <button
            type="submit"
            disabled={pwLoading}
            style={{
              width: '100%', padding: '12px 24px', background: '#010E23', color: 'white',
              border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600,
              cursor: pwLoading ? 'not-allowed' : 'pointer', opacity: pwLoading ? 0.8 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: 'all 0.2s ease',
            }}
          >
            {pwLoading && (
              <div className="animate-spin" style={{
                width: 18, height: 18, borderRadius: '50%',
                border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: 'white',
              }} />
            )}
            {pwLoading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {/* Secured by Microsoft Azure AD */}
        <p style={{
          fontSize: 11,
          color: '#9ca3af',
          textAlign: 'center',
          marginTop: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="#9ca3af" aria-hidden>
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
          </svg>
          Secured by Microsoft Azure AD
        </p>
      </div>

      {/* Copyright footer */}
      <p style={{
        position: 'absolute',
        bottom: 24,
        fontSize: 11,
        color: 'rgba(255,255,255,0.3)',
        textAlign: 'center',
        pointerEvents: 'none',
      }}>
        © {new Date().getFullYear()} Permus Software House. All rights reserved.
      </p>
    </div>
  );
}
